import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { rideAPI, riderAPI } from '../../lib/api';
import { formatCurrency } from '../../lib/utils';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Label } from '../../components/ui/label';
import LocationPicker from '../../components/LocationPicker';
import { MapPin, Navigation, DollarSign, Clock, Loader2, Gift } from 'lucide-react';
import { toast } from 'sonner';

const BookRide = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [estimating, setEstimating] = useState(false);
  const [estimate, setEstimate] = useState(null);
  const [userLocation, setUserLocation] = useState(null);
  const [pickup, setPickup] = useState({ lat: null, lng: null, address: '' });
  const [dropoff, setDropoff] = useState({ lat: null, lng: null, address: '' });
  const [loyalty, setLoyalty] = useState(null);

  useEffect(() => {
    // Get user's current location
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          const loc = {
            lat: position.coords.latitude,
            lng: position.coords.longitude,
          };
          setUserLocation(loc);
        },
        (error) => {
          console.log('Geolocation error:', error);
        }
      );
    }

    // Load loyalty info
    loadLoyalty();
  }, []);

  const loadLoyalty = async () => {
    try {
      const response = await riderAPI.getLoyalty();
      setLoyalty(response.data);
    } catch (error) {
      console.error('Error loading loyalty:', error);
    }
  };

  const getEstimate = useCallback(async () => {
    if (!pickup.lat || !pickup.lng || !dropoff.lat || !dropoff.lng) return;

    setEstimating(true);
    try {
      const response = await rideAPI.getEstimate({
        pickup_location: { lat: pickup.lat, lng: pickup.lng, address: pickup.address },
        dropoff_location: { lat: dropoff.lat, lng: dropoff.lng, address: dropoff.address },
      });
      setEstimate(response.data);
    } catch (error) {
      console.error('Error getting estimate:', error);
      toast.error('Failed to get fare estimate');
    } finally {
      setEstimating(false);
    }
  }, [pickup, dropoff]);

  useEffect(() => {
    if (pickup.lat && pickup.lng && dropoff.lat && dropoff.lng) {
      getEstimate();
    }
  }, [pickup.lat, pickup.lng, dropoff.lat, dropoff.lng, getEstimate]);

  const handlePickupSelect = (location) => {
    setPickup(location);
  };

  const handleDropoffSelect = (location) => {
    setDropoff(location);
  };

  const requestRide = async () => {
    if (!estimate) return;

    setLoading(true);
    try {
      const response = await rideAPI.requestRide({
        pickup_location: { lat: pickup.lat, lng: pickup.lng, address: pickup.address },
        dropoff_location: { lat: dropoff.lat, lng: dropoff.lng, address: dropoff.address },
      });
      
      toast.success('Ride requested! Looking for a driver...');
      navigate(`/rider/ride/${response.data.ride.id}`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to request ride');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="p-6" data-testid="book-ride-page">
      <div className="max-w-2xl mx-auto">
        <h1 className="text-3xl font-display font-bold text-slate-900 dark:text-white uppercase tracking-tight mb-8">
          Book a Ride
        </h1>

        <div className="space-y-6">
          {/* Location Inputs */}
          <Card className="bg-white dark:bg-slate-800 border-slate-200 dark:border-slate-700">
            <CardContent className="p-6">
              <div className="space-y-4">
                {/* Pickup */}
                <div className="space-y-2">
                  <Label className="flex items-center gap-2">
                    <div className="w-3 h-3 bg-green-500 rounded-full"></div>
                    Pickup Location
                  </Label>
                  <LocationPicker
                    value={pickup.address}
                    onChange={(val) => setPickup(prev => ({ ...prev, address: val }))}
                    onSelect={handlePickupSelect}
                    placeholder="Enter pickup location"
                    icon={MapPin}
                    iconColor="text-green-500"
                    showGpsButton={true}
                    showMapPicker={true}
                    data-testid="pickup-input"
                  />
                </div>

                {/* Visual connector */}
                <div className="flex items-center gap-3 py-2">
                  <div className="w-5 flex flex-col items-center">
                    <div className="w-0.5 h-8 bg-slate-200 dark:bg-slate-600"></div>
                  </div>
                </div>

                {/* Dropoff */}
                <div className="space-y-2">
                  <Label className="flex items-center gap-2">
                    <div className="w-3 h-3 bg-red-500 rounded-full"></div>
                    Dropoff Location
                  </Label>
                  <LocationPicker
                    value={dropoff.address}
                    onChange={(val) => setDropoff(prev => ({ ...prev, address: val }))}
                    onSelect={handleDropoffSelect}
                    placeholder="Where to?"
                    icon={MapPin}
                    iconColor="text-red-500"
                    showGpsButton={false}
                    showMapPicker={true}
                    data-testid="dropoff-input"
                  />
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Fare Estimate */}
          {(estimating || estimate) && (
            <Card className="bg-white dark:bg-slate-800 border-slate-200 dark:border-slate-700">
              <CardHeader>
                <CardTitle className="font-display uppercase tracking-tight flex items-center gap-2">
                  <DollarSign className="w-5 h-5 text-red-600" />
                  Fare Estimate
                </CardTitle>
              </CardHeader>
              <CardContent>
                {estimating ? (
                  <div className="flex items-center justify-center py-8">
                    <Loader2 className="w-8 h-8 animate-spin text-red-600" />
                  </div>
                ) : estimate ? (
                  <div className="space-y-4">
                    {/* Main fare */}
                    <div className="flex items-center justify-between">
                      <span className="text-slate-500 dark:text-slate-400">Estimated Fare</span>
                      <span className="text-4xl font-display font-bold text-slate-900 dark:text-white">
                        {formatCurrency(estimate.subtotal)}
                      </span>
                    </div>

                    {/* Breakdown */}
                    <div className="space-y-2 pt-4 border-t border-slate-100 dark:border-slate-700">
                      <div className="flex justify-between text-sm">
                        <span className="text-slate-500 dark:text-slate-400">
                          Base fare ({estimate.distance_miles} mi)
                        </span>
                        <span className="text-slate-900 dark:text-white">{formatCurrency(estimate.mileage_cost)}</span>
                      </div>
                      <div className="flex justify-between text-sm">
                        <span className="text-slate-500 dark:text-slate-400">Service fee</span>
                        <span className="text-slate-900 dark:text-white">{formatCurrency(estimate.service_fee)}</span>
                      </div>
                      {estimate.loyalty_discount > 0 && (
                        <div className="flex justify-between text-sm text-green-600">
                          <span className="flex items-center gap-1">
                            <Gift className="w-4 h-4" />
                            Loyalty discount ({estimate.loyalty_discount_percent}%)
                          </span>
                          <span>-{formatCurrency(estimate.loyalty_discount)}</span>
                        </div>
                      )}
                    </div>

                    {/* Trip info */}
                    <div className="flex items-center gap-6 pt-4 border-t border-slate-100 dark:border-slate-700">
                      <div className="flex items-center gap-2 text-slate-500 dark:text-slate-400">
                        <Navigation className="w-4 h-4" />
                        <span>{estimate.distance_miles} mi</span>
                      </div>
                      <div className="flex items-center gap-2 text-slate-500 dark:text-slate-400">
                        <Clock className="w-4 h-4" />
                        <span>~{estimate.duration_minutes} min</span>
                      </div>
                    </div>
                  </div>
                ) : null}
              </CardContent>
            </Card>
          )}

          {/* Request Button */}
          <Button
            onClick={requestRide}
            disabled={loading || !estimate}
            className="w-full h-14 rounded-full bg-red-600 hover:bg-red-700 font-bold uppercase tracking-wider text-lg shadow-lg shadow-red-600/20"
            data-testid="request-ride-btn"
          >
            {loading ? (
              <Loader2 className="w-6 h-6 animate-spin" />
            ) : (
              <>Request Ride {estimate && `• ${formatCurrency(estimate.subtotal)}`}</>
            )}
          </Button>

          {/* Loyalty reminder */}
          {loyalty && loyalty.discount_percent > 0 && (
            <p className="text-center text-sm text-green-600 dark:text-green-400">
              <Gift className="w-4 h-4 inline mr-1" />
              {loyalty.current_tier} member - {loyalty.discount_percent}% off applied!
            </p>
          )}
        </div>
      </div>
    </div>
  );
};

export default BookRide;
